import { Link, useLocation } from '@tanstack/react-router';
import type { LucideIcon } from 'lucide-react';

import { cn } from '~/utils/cn';

interface NavItemProps {
  /** Route path */
  to: string;
  /** Lucide icon component */
  icon: LucideIcon;
  /** Display label */
  label: string;
  /**
   * When true only the icon shows (rail collapsed). When false the label is
   * revealed to the right of the icon. The icon lives in a fixed-width leading
   * zone so it stays in the exact same position in either state.
   */
  collapsed?: boolean | undefined;
  /**
   * When set, the nav item stays highlighted for any route whose pathname
   * starts with this prefix (e.g. `/organization` highlights for `/organization/roles`, `/organization/sso`, etc.).
   * The link still navigates to `to`.
   */
  activeBasePath?: string;
  /**
   * Additional path prefixes that also keep this item highlighted — for flows
   * routed under a different URL root than the nav entry that owns them (e.g.
   * the content-enrichment flow is entered from Catalog Operations but lives
   * under `/enrichments/*`). Without these the sidebar highlights nothing there.
   */
  extraActivePaths?: string[];
  /** Small pill after the label (e.g. "Preview") for a section still in preview. Shown only
   *  while the rail is expanded; folded into the accessible name when collapsed. */
  tag?: string | undefined;
}

/** True when `pathname` is `base` or sits beneath it. */
function isUnder(pathname: string, base: string): boolean {
  return pathname === base || pathname.startsWith(base + '/');
}

/**
 * Single navigation item for the sidebar. The icon is kept in a fixed 72px
 * leading zone (matching the collapsed rail width) so expanding the rail only
 * reveals the label to its right without shifting the icon's position.
 */
export function NavItem({
  to,
  icon: Icon,
  label,
  collapsed = false,
  activeBasePath,
  extraActivePaths,
  tag,
}: NavItemProps) {
  const { pathname } = useLocation();
  const isActive =
    isUnder(pathname, activeBasePath ?? to) ||
    (extraActivePaths ?? []).some((base) => isUnder(pathname, base));

  return (
    <Link
      to={to}
      aria-label={collapsed ? (tag ? `${label} (${tag})` : label) : undefined}
      aria-current={isActive ? 'page' : undefined}
      className="group/nav flex w-full cursor-pointer items-center text-sm font-medium"
    >
      {/* Fixed 72px icon zone — keeps the icon in the same x-position whether
          the rail is collapsed or expanded. */}
      <span className="flex w-18 shrink-0 items-center justify-center py-0.5">
        <span
          className={cn(
            'flex h-10 w-10 items-center justify-center rounded-xl transition-colors',
            isActive
              ? '[&_svg]:text-nav-active shadow-nav-active bg-white'
              : '[&_svg]:text-nav-icon group-hover/nav:bg-white/60',
          )}
        >
          <Icon aria-hidden="true" className="h-5 w-5 shrink-0" />
        </span>
      </span>
      {!collapsed && (
        // A tagged item trims its right padding and the pill's so the label still fits in full.
        <span className={cn('flex min-w-0 items-center', tag ? 'gap-1.5 pr-2' : 'pr-4')}>
          <span className={cn('truncate', isActive ? 'text-nav-active' : 'text-nav-foreground')}>
            {label}
          </span>
          {tag && (
            <span
              className="bg-accent-100 text-primary shrink-0 rounded-full px-1.5 py-0.5 text-[10px] leading-none font-medium"
              data-testid="nav-item-tag"
            >
              {tag}
            </span>
          )}
        </span>
      )}
    </Link>
  );
}
